import React from 'react';

import { useRecordContext } from 'react-admin';
import { Box, Grid, Typography } from '@mui/material';

import auditTypes from '../../constants/auditTypes';
import {
  IpExtraInfo,
  ConnectExtraInfo,
  DisconnectExtraInfo,
  PingCreateExtraInfo,
  CaseCreateExtraInfo,
  PingTagsUpdateExtraInfo,
  PingStatusExtraInfo,
  PingReprocessExtraInfo,
  CommentExtraInfo,
  WebhookConfigCreateExtraInfo,
  WebhookConfigUpdateExtraInfo,
  DataPointCreateExtraInfo,
  ReportGenerateExtraInfo,
  FormLinkGenerateExtraInfo,
  SaveTransactionSnapshotExtraInfo,
} from '../../utilities/schemas/auditTrackExtraInfo';
import IAuditTrack from '../../utilities/schemas/auditTrack';

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid size={{ xs: 12, md: 6, xl: 3 }}>
    <Box display="flex" flexDirection="column">
      <Typography variant="mediumTitle">{label}</Typography>
      <Typography variant="body2">{String(value ?? '-')}</Typography>
    </Box>
  </Grid>
);

const IpInfo = ({ extraInfo }: { extraInfo: IpExtraInfo }) => (
  <Row label="IP" value={extraInfo.ip} />
);

const ConnectInfo = ({ extraInfo }: { extraInfo: ConnectExtraInfo }) => (
  <>
    <Row label="Connection ID" value={extraInfo.id} />
    <Row label="From" value={`${extraInfo.from.type} / ${extraInfo.from.id}`} />
    <Row label="To" value={`${extraInfo.to.type} / ${extraInfo.to.id}`} />
    {extraInfo.description && (
      <Row label="Description" value={extraInfo.description} />
    )}
  </>
);

const DisconnectInfo = ({ extraInfo }: { extraInfo: DisconnectExtraInfo }) => {
  if ('from' in extraInfo) {
    return <Row label="From" value={`${extraInfo.from.type} / ${extraInfo.from.id}`} />;
  }
  return <Row label="To" value={`${extraInfo.to.type} / ${extraInfo.to.id}`} />;
};

const PingCreateInfo = ({ extraInfo }: { extraInfo: PingCreateExtraInfo }) => (
  <>
    <Row label="Case ID" value={extraInfo.caseId} />
    <Row label="Description" value={extraInfo.description} />
    <Row label="Score" value={extraInfo.score} />
    <Row
      label="Entities"
      value={extraInfo.entities
        .map((e) => `${e.type} / ${e.id}`)
        .join(', ')}
    />
    <Row
      label="Risk timeline"
      value={`${extraInfo.riskTimeline.period}: ${extraInfo.riskTimeline.amount}`}
    />
  </>
);

const CaseCreateInfo = ({ extraInfo }: { extraInfo: CaseCreateExtraInfo }) => (
  <>
    <Row
      label="Entities"
      value={extraInfo.entities
        .map((e) => `${e.type} / ${e.id}`)
        .join(', ')}
    />
    {extraInfo.statusHistory?.[0] && (
      <Row label="Status" value={extraInfo.statusHistory[0].status} />
    )}
    {extraInfo.assignment && (
      <Row label="Assignee" value={extraInfo.assignment.memberId ?? 'Unassigned'} />
    )}
    {extraInfo.pings && extraInfo.pings.length > 0 && (
      <Row
        label="Pings"
        value={extraInfo.pings.map((p) => p.id).join(', ')}
      />
    )}
    {extraInfo.requests && extraInfo.requests.length > 0 && (
      <Row label="Requests" value={extraInfo.requests.join(', ')} />
    )}
    {extraInfo.tags && extraInfo.tags.length > 0 && (
      <Row label="Tags" value={extraInfo.tags.join(', ')} />
    )}
  </>
);

const PingTagsUpdateInfo = ({
  extraInfo,
}: {
  extraInfo: PingTagsUpdateExtraInfo;
}) => <Row label="Tags" value={extraInfo.tags.join(', ')} />;

const PingStatusInfo = ({ extraInfo }: { extraInfo: PingStatusExtraInfo }) => (
  <>
    <Row label="Status" value={extraInfo.status} />
    {extraInfo.comment && <Row label="Comment" value={extraInfo.comment} />}
    {extraInfo.score !== undefined && (
      <Row label="Score" value={extraInfo.score} />
    )}
    {extraInfo.overrideRecommendation && (
      <Row
        label="Override recommendation"
        value={extraInfo.overrideRecommendation}
      />
    )}
    {extraInfo.numberOfDaysIgnoreRule !== undefined && (
      <Row
        label="Ignore rule (days)"
        value={extraInfo.numberOfDaysIgnoreRule}
      />
    )}
  </>
);

const PingReprocessInfo = ({
  extraInfo,
}: {
  extraInfo: PingReprocessExtraInfo;
}) => (
  <>
    <Row label="Request ID" value={extraInfo.requestId} />
    <Row label="Comment" value={extraInfo.comment} />
  </>
);

const CommentInfo = ({ extraInfo }: { extraInfo: CommentExtraInfo }) => (
  <>
    <Row label="Comment" value={extraInfo.comment} />
    {extraInfo.origin && <Row label="Origin" value={extraInfo.origin} />}
  </>
);

const WebhookConfigCreateInfo = ({
  extraInfo,
}: {
  extraInfo: WebhookConfigCreateExtraInfo;
}) => {
  const { deliverySettings } = extraInfo;
  return (
    <>
      <Row label="URL" value={deliverySettings.url} />
      {deliverySettings.idempotencyHeader && (
        <Row
          label="Idempotency header"
          value={deliverySettings.idempotencyHeader}
        />
      )}
      {deliverySettings.timeout !== undefined && (
        <Row label="Timeout" value={deliverySettings.timeout} />
      )}
    </>
  );
};

const WebhookConfigUpdateInfo = ({
  extraInfo,
}: {
  extraInfo: WebhookConfigUpdateExtraInfo;
}) => {
  const ds = extraInfo.deliverySettings;
  if (!ds) return null;
  return (
    <>
      {ds.url && <Row label="URL" value={ds.url} />}
      {ds.idempotencyHeader && (
        <Row label="Idempotency header" value={ds.idempotencyHeader} />
      )}
      {ds.timeout !== undefined && <Row label="Timeout" value={ds.timeout} />}
    </>
  );
};

const DataPointCreateInfo = ({
  extraInfo,
}: {
  extraInfo: DataPointCreateExtraInfo;
}) => (
  <>
    <Row label="Entity" value={`${extraInfo.entity.type} / ${extraInfo.entity.id}`} />
    <Row label="Name" value={extraInfo.name} />
    <Row label="Type" value={extraInfo.type} />
    <Row label="Value" value={JSON.stringify(extraInfo.value)} />
    {extraInfo.provider && <Row label="Provider" value={extraInfo.provider} />}
    {extraInfo.createdBy && (
      <Row label="Created by" value={extraInfo.createdBy} />
    )}
    {extraInfo.comment && <Row label="Comment" value={extraInfo.comment} />}
  </>
);

const ReportGenerateInfo = ({
  extraInfo,
}: {
  extraInfo: ReportGenerateExtraInfo;
}) => <Row label="Type" value={extraInfo.type} />;

const FormLinkGenerateInfo = ({
  extraInfo,
}: {
  extraInfo: FormLinkGenerateExtraInfo;
}) => <Row label="Form ID" value={extraInfo.formId} />;

const TransactionSnapshotInfo = ({
  extraInfo,
}: {
  extraInfo: SaveTransactionSnapshotExtraInfo;
}) => <Row label="Snapshot ID" value={extraInfo.snapshotId} />;

const AuditExtraInfo = () => {
  const record = useRecordContext<IAuditTrack>();
  if (!record?.auditType || !record.extraInfo) return null;

  const { auditType, extraInfo } = record;

  switch (auditType) {
    case auditTypes.LOGIN:
    case auditTypes.CLIENT_SECRET_DISPLAYED:
      return (
        <Grid container spacing={6}>
          <IpInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.CONNECT:
      return (
        <Grid container spacing={6}>
          <ConnectInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.DISCONNECT:
      return (
        <Grid container spacing={6}>
          <DisconnectInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.CREATE: {
      if ('riskTimeline' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <PingCreateInfo extraInfo={extraInfo as PingCreateExtraInfo} />
          </Grid>
        );
      }
      if ('entities' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <CaseCreateInfo extraInfo={extraInfo as CaseCreateExtraInfo} />
          </Grid>
        );
      }
      if ('deliverySettings' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <WebhookConfigCreateInfo extraInfo={extraInfo as WebhookConfigCreateExtraInfo} />
          </Grid>
        );
      }
      if ('entity' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <DataPointCreateInfo extraInfo={extraInfo as DataPointCreateExtraInfo} />
          </Grid>
        );
      }
      return null;
    }

    case auditTypes.UPDATE: {
      if ('tags' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <PingTagsUpdateInfo extraInfo={extraInfo as PingTagsUpdateExtraInfo} />
          </Grid>
        );
      }
      if ('deliverySettings' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <WebhookConfigUpdateInfo extraInfo={extraInfo as WebhookConfigUpdateExtraInfo} />
          </Grid>
        );
      }
      return null;
    }

    case auditTypes.GENERATE:
      return (
        <Grid container spacing={6}>
          <ReportGenerateInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.GENERATE_FORM_LINK:
      return (
        <Grid container spacing={6}>
          <FormLinkGenerateInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.STATUS: {
      if ('status' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <PingStatusInfo extraInfo={extraInfo as PingStatusExtraInfo} />
          </Grid>
        );
      }
      if ('requestId' in extraInfo) {
        return (
          <Grid container spacing={6}>
            <PingReprocessInfo extraInfo={extraInfo as PingReprocessExtraInfo} />
          </Grid>
        );
      }
      return null;
    }

    case auditTypes.COMMENT:
    case auditTypes.COMMENT_EDITED:
      return (
        <Grid container spacing={6}>
          <CommentInfo extraInfo={extraInfo} />
        </Grid>
      );

    case auditTypes.SAVE_TRANSACTION_SNAPSHOT:
    case auditTypes.DELETE_TRANSACTION_SNAPSHOT:
      return (
        <Grid container spacing={6}>
          <TransactionSnapshotInfo extraInfo={extraInfo as SaveTransactionSnapshotExtraInfo} />
        </Grid>
      );

    default:
      return null;
  }
};

export default AuditExtraInfo;
